import { z } from 'zod';
import { PACING_INTERVAL_MAX_MS, PACING_INTERVAL_MIN_MS, EVAL_RUN_DEADLINE_MIN_MS, EVAL_RUN_DEADLINE_MAX_MS } from '../eval-pacing';

// ---------------------------------------------------------------------------
// Common
// ---------------------------------------------------------------------------

const paginationSchema = z.object({
  limit: z.number().int().positive().max(1000).default(50),
  offset: z.number().int().min(0).default(0),
}).optional();

const evalIdParam = z.object({
  id: z.string().uuid(),
});

const sessionIdParam = z.object({
  id: z.string().uuid(),
});

const scenarioSetIdParam = z.object({
  id: z.string().uuid(),
});

const hubIdQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Eval CRUD
// ---------------------------------------------------------------------------

export const createEvalBody = z.object({
  eval: z.record(z.unknown()),
});

export const updateEvalBody = z.object({
  eval: z.record(z.unknown()),
});

export const getEvalsQuery = z.object({
  hub_id: z.string().uuid(),
  agent_id: z.string().uuid().optional(),
  enabled: z.string().regex(/^(true|false)$/).optional(),
  search: z.string().optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Toggle / Bulk Toggle
// ---------------------------------------------------------------------------

export const toggleEvalBody = z.object({
  enabled: z.boolean(),
});

export const bulkToggleEvalsBody = z.object({
  hub_id: z.string().uuid(),
  enabled: z.boolean(),
  filters: z.object({
    agent_id: z.string().uuid().optional(),
    search: z.string().optional(),
  }).optional(),
});

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

export const pacingConfigSchema = z.object({
  preset: z.enum(['conservative', 'custom']),
  custom_interval_ms: z.number().int().min(PACING_INTERVAL_MIN_MS).max(PACING_INTERVAL_MAX_MS).optional(),
}).refine(
  (v) => v.preset !== 'custom' || typeof v.custom_interval_ms === 'number',
  { message: 'custom_interval_ms required when preset is "custom"' },
);

// `pacing` is the only client-supplied session_config field today; the backend
// also writes `agent` and `scenario_set_id` snapshots during run-session.
// `passthrough()` on both layers preserves those server-managed fields and
// keeps room for future client-side keys without contract churn.
export const sessionConfigSchema = z.object({
  pacing: pacingConfigSchema.optional(),
  // Per-run finalization deadline for the HubDO watchdog (gh #2591). A started
  // run idle past this many ms is reaped to `failed`. Omitted → backend default
  // (`EVAL_DEFAULT_RUN_DEADLINE_MS`).
  run_deadline_ms: z.number().int().min(EVAL_RUN_DEADLINE_MIN_MS).max(EVAL_RUN_DEADLINE_MAX_MS).optional(),
}).passthrough();

export const createSessionBody = z.object({
  session: z.object({
    session_config: sessionConfigSchema.optional(),
  }).passthrough(),
});

export const getSessionsQuery = z.object({
  hub_id: z.string().uuid(),
  status: z.string().optional(),
  search: z.string().optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Results & Runs
// ---------------------------------------------------------------------------

export const getSessionResultsQuery = z.object({
  hub_id: z.string().uuid(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
});

export const getSessionRunsQuery = z.object({
  hub_id: z.string().uuid(),
  eval_id: z.string().uuid().optional(),
  limit: z.string().regex(/^\d+$/).optional(),
  offset: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export const evalAnalyticsQuery = z.object({
  hub_id: z.string().uuid(),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  periodicity: z.enum(['daily', 'weekly', 'monthly']).optional(),
  eval_id: z.string().uuid().optional(),
  session_id: z.string().uuid().optional(),
});

export const getSessionCostQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Compare sessions
// ---------------------------------------------------------------------------

export const compareSessionsQuery = z.object({
  hub_id: z.string().uuid(),
  session_a: z.string().uuid(),
  session_b: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Read-only SQL (CLI agent surface — POST /sql, GET /sql/schema)
// Scopes the conversation table to `is_eval = true` (mirrors /api/analytics/sql).
// ---------------------------------------------------------------------------

export const evalsSqlBody = z.object({
  hub_id: z.string().uuid(),
  // Hard size cap before the validator runs — keeps lex/parse work bounded.
  sql: z.string().min(1).max(8000),
  limit: z.number().int().positive().max(10000).optional(),
});

export const evalsSqlSchemaQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const exportResultsQuery = z.object({
  format: z.enum(['csv', 'json']).default('csv'),
});

// ---------------------------------------------------------------------------
// Validate
// ---------------------------------------------------------------------------

export const validateEvalBody = z.object({
  eval: z.record(z.unknown()),
});

// ---------------------------------------------------------------------------
// Hub Agents
// ---------------------------------------------------------------------------

export const hubAgentsQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Scenario sets
// ---------------------------------------------------------------------------

export const createScenarioSetBody = z.object({
  hub_id: z.string().uuid(),
  set_name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  enabled: z.boolean().optional(),
});

export const updateScenarioSetBody = z.object({
  set_name: z.string().min(1).max(200).optional(),
  description: z.string().max(1000).nullable().optional(),
  enabled: z.boolean().optional(),
});

export const scenarioSetsHubQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Production replay — create scenario from a real conversation
// ---------------------------------------------------------------------------

export const createScenarioFromConversationBody = z.object({
  hub_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  scenario_set_id: z.string().uuid(),
  scenario_name: z.string().min(1).max(200),
  evaluator_instructions: z.string().max(4000).optional(),
});

// Capture a real conversation as an eval JOURNEY — the WHOLE transcript, not
// just the last exchange. A journey owns its managed scenario set, so there is
// no `scenario_set_id` here (cf. the scenario body above).
export const createJourneyFromConversationBody = z.object({
  hub_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  journey_name: z.string().min(1).max(200),
  evaluator_instructions: z.string().max(4000).optional(),
});

// ---------------------------------------------------------------------------
// Eval journeys — a happy-path transcript that materializes into single-turn evals
// ---------------------------------------------------------------------------

const journeyIdParam = z.object({ id: z.string().uuid() });

const journeyToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('function'),
  function: z.object({ name: z.string(), arguments: z.string() }),
});

// One transcript turn — the replayed message-history shape plus an optional
// stable `step_id` (server-minted on each step's concluding assistant turn).
const journeyTurnSchema = z.object({
  role: z.enum(['system', 'user', 'assistant', 'tool']),
  content: z.string().nullable().optional(),
  tool_calls: z.array(journeyToolCallSchema).optional(),
  tool_call_id: z.string().optional(),
  step_id: z.string().optional(),
});

const journeyStepOverrideSchema = z.object({
  number_of_runs: z.number().int().positive().max(100).optional(),
  evaluator_instructions: z.string().max(4000).nullable().optional(),
});

export const createJourneyBody = z.object({
  hub_id: z.string().uuid(),
  journey_name: z.string().min(1).max(200),
  responder_agent_id: z.string().uuid(),
  transcript: z.array(journeyTurnSchema).default([]),
  step_config: z.record(journeyStepOverrideSchema).optional(),
  evaluator_instructions: z.string().max(4000).optional(),
});

export const updateJourneyBody = z.object({
  hub_id: z.string().uuid(),
  journey_name: z.string().min(1).max(200).optional(),
  responder_agent_id: z.string().uuid().optional(),
  transcript: z.array(journeyTurnSchema).optional(),
  step_config: z.record(journeyStepOverrideSchema).optional(),
  evaluator_instructions: z.string().max(4000).nullable().optional(),
  enabled: z.boolean().optional(),
});

// (journey list reuses the shared `hubIdQuery`)

// Re-export param schemas
export { evalIdParam, sessionIdParam, scenarioSetIdParam, hubIdQuery, journeyIdParam };

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type EvalIdParam = z.infer<typeof evalIdParam>;
export type SessionIdParam = z.infer<typeof sessionIdParam>;
export type ScenarioSetIdParam = z.infer<typeof scenarioSetIdParam>;
export type CreateEvalBody = z.infer<typeof createEvalBody>;
export type UpdateEvalBody = z.infer<typeof updateEvalBody>;
export type GetEvalsQuery = z.infer<typeof getEvalsQuery>;
export type ToggleEvalBody = z.infer<typeof toggleEvalBody>;
export type BulkToggleEvalsBody = z.infer<typeof bulkToggleEvalsBody>;
export type CreateSessionBody = z.infer<typeof createSessionBody>;
export type GetSessionsQuery = z.infer<typeof getSessionsQuery>;
export type GetSessionResultsQuery = z.infer<typeof getSessionResultsQuery>;
export type GetSessionRunsQuery = z.infer<typeof getSessionRunsQuery>;
export type EvalAnalyticsQuery = z.infer<typeof evalAnalyticsQuery>;
export type ExportResultsQuery = z.infer<typeof exportResultsQuery>;
export type ValidateEvalBody = z.infer<typeof validateEvalBody>;
export type HubAgentsQuery = z.infer<typeof hubAgentsQuery>;
export type CreateScenarioSetBody = z.infer<typeof createScenarioSetBody>;
export type UpdateScenarioSetBody = z.infer<typeof updateScenarioSetBody>;
export type ScenarioSetsHubQuery = z.infer<typeof scenarioSetsHubQuery>;
export type CompareSessionsQuery = z.infer<typeof compareSessionsQuery>;
export type CreateScenarioFromConversationBody = z.infer<typeof createScenarioFromConversationBody>;
export type CreateJourneyFromConversationBody = z.infer<typeof createJourneyFromConversationBody>;
export type EvalsSqlBody = z.infer<typeof evalsSqlBody>;
export type EvalsSqlSchemaQuery = z.infer<typeof evalsSqlSchemaQuery>;
export type JourneyIdParam = z.infer<typeof journeyIdParam>;
export type CreateJourneyBody = z.infer<typeof createJourneyBody>;
export type UpdateJourneyBody = z.infer<typeof updateJourneyBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

// -- Status types --

export type EvalStatus = 'enabled' | 'disabled';
export type SessionStatus = 'draft' | 'agents_responding' | 'awaiting_review' | 'completed' | 'failed' | 'cancelled';
export type RunStatus = 'pending' | 'running' | 'agent_responded' | 'completed' | 'failed';

// -- Tool call structures --

export interface ToolCallResponse {
  id: string;
  type: 'function';
  function: {
    name: string;
    arguments: string;
  };
}

// -- Message structures --

export interface MessageHistoryItemResponse {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | null;
  tool_calls?: ToolCallResponse[];
  tool_call_id?: string;
}

export interface ExpectedResponseData {
  role: 'assistant';
  content: string | null;
  tool_calls?: ToolCallResponse[];
}

export interface EvalResponseData {
  role: 'assistant';
  content: string;
  /**
   * The tool calls the agent made this turn (name + arguments). Present for
   * tool-call evals so `eval-results --runs` is self-contained — the verdict,
   * the reasoning, AND the actual call — without opening the conversation
   * (gh #2597). Omitted when the agent made no tool calls. Same shape as
   * `ExpectedResponseData.tool_calls` so expected vs. actual can be diffed.
   */
  tool_calls?: ToolCallResponse[];
  metadata?: {
    response_time_ms?: number;
    model_used?: string;
    tokens_used?: number;
  };
}

// -- Entity response types --

/** Full eval row as returned by the eval table. Scenarios MUST belong to a scenario set. */
export interface EvalResponse {
  eval_id: string;
  created_at: string;
  updated_at: string;
  hub_id: string;
  user_id: string;
  responder_agent_id: string;
  eval_name: string;
  scenario_set_id: string;
  evaluator_instructions?: string;
  message_history: MessageHistoryItemResponse[];
  message_text: MessageHistoryItemResponse;
  message_expected_response: ExpectedResponseData;
  number_of_runs: number;
  enabled: boolean;
  created_by: string;
  /** Non-null for evals derived from an eval journey (server-managed). */
  journey_id?: string | null;
  /** 0-based display order of the step within its journey (for step-by-step reports). */
  journey_step_index?: number | null;
}

/** One turn of a journey's happy-path transcript (message-history shape + optional step_id). */
export interface JourneyTranscriptTurnResponse extends MessageHistoryItemResponse {
  step_id?: string;
}

/** Per-step override config, keyed by step_id. */
export interface JourneyStepOverrideResponse {
  number_of_runs?: number;
  evaluator_instructions?: string | null;
}

/** Full eval_journey row. */
export interface JourneyResponse {
  journey_id: string;
  journey_name: string;
  scenario_set_id: string;
  responder_agent_id: string;
  transcript: JourneyTranscriptTurnResponse[];
  step_config: Record<string, JourneyStepOverrideResponse>;
  evaluator_instructions?: string | null;
  enabled: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
}

/** Frozen agent configuration captured at session-launch time. */
export interface AgentConfigSnapshot {
  agent_id: string;
  agent_name: string | null;
  agent_role: string | null;
  instructions: string | null;
  response_format: string | null;
  schema_name: string | null;
  schema_json: unknown;
  agent_settings: unknown;
  enabled: boolean;
  connection: { connection_id: string | null; connector_id: string | null; model_name: string | null } | null;
  tools: Array<Record<string, unknown>>;
  resources: Array<{ resource_id: string; version?: string | null }>;
}

/** Frozen scenario content captured per `eval_session_run`. */
export interface ScenarioSnapshot {
  message_text: unknown;
  message_expected_response: unknown;
  message_history: unknown;
  evaluator_instructions: string | null;
}

/** Internal RPC input for `HubDO.completeEvalRun` (not a public route). */
export interface CompleteEvalRunInput {
  run_id: string;
  status: 'completed' | 'failed';
  agent_response?: string;
  /** Tool calls the agent made this turn — persisted alongside `agent_response` (gh #2597). */
  agent_tool_calls?: ToolCallResponse[];
  execution_time_ms: number;
  error_message?: string;
  score_dimensions?: Record<string, unknown>;
  response_match?: boolean | null;
  /**
   * The responder's evaluated assistant message id and the message_evaluator's
   * verdict message id, both in the run's eval conversation. Persisted on the run
   * so consumers can jump straight to a turn's LLM observability
   * (`wayai conversations <conv> observability --message-id <id>`) without first
   * listing the conversation. Omitted on the failed path (no turn produced).
   */
  response_message_id?: string;
  evaluator_message_id?: string;
}

/** Eval scenario set row. */
export interface ScenarioSetResponse {
  scenario_set_id: string;
  set_name: string;
  description?: string | null;
  enabled: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at?: string;
  /** Non-null for sets owned by an eval journey (server-managed, read-only in the UI). */
  journey_id?: string | null;
}

/** Eval with joined agent data, as returned by list endpoint */
export interface EvalWithAgentResponse extends EvalResponse {
  agent?: {
    agent_id: string;
    agent_name: string;
    agent_role: string;
  } | null;
}

/** Eval session row */
export interface EvalSessionResponse {
  eval_session_id: string;
  created_at: string;
  updated_at: string;
  hub_id: string;
  session_name: string;
  session_description?: string;
  session_status: SessionStatus;
  started_at?: string;
  completed_at?: string;
  total_evals: number;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  created_by: string;
  /** Optional — sessions created pre-PR2 may not have this. */
  scenario_set_id?: string | null;
  /**
   * Snapshot of the responder agent + scenario_set_id captured at session-launch time.
   * `agent` may be missing on sessions created pre-PR2; consumers must render gracefully.
   */
  session_config?: {
    agent?: AgentConfigSnapshot;
    scenario_set_id?: string;
    pacing?: {
      preset: 'conservative' | 'custom';
      custom_interval_ms?: number;
    };
  };
}

/** Session result row with optional joined eval */
export interface EvalSessionResultResponse {
  eval_session_result_id: string;
  created_at: string;
  updated_at: string;
  eval_session_id: string;
  eval_id: string;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  pending_runs: number;
  running_runs: number;
  avg_execution_time_ms?: number;
  min_execution_time_ms?: number;
  max_execution_time_ms?: number;
  aggregated_scores?: Record<string, { avg: number; min: number; max: number }>;
  success_rate: number;
  eval_status: RunStatus;
  started_at?: string;
  completed_at?: string;
  eval?: EvalResponse | null;
}

/** Individual run result row with optional joined eval */
export interface EvalSessionRunResponse {
  eval_session_run_id: string;
  created_at: string;
  updated_at: string;
  eval_session_id: string;
  eval_id: string;
  conversation_id?: string;
  /**
   * The responder's evaluated assistant message id and the message_evaluator's
   * verdict message id within `conversation_id`. Let consumers (CLI, CI) drill
   * straight into a turn's LLM observability without listing the conversation
   * first. Absent on runs that predate this capture, or that never produced the
   * respective turn (e.g. a failed run has no evaluator message).
   */
  response_message_id?: string;
  evaluator_message_id?: string;
  run_number: number;
  eval_response: EvalResponseData;
  /**
   * The expected response frozen on the run's `scenario_snapshot` at
   * materialize time (NOT the live eval row, which may have drifted since). Lets
   * consumers render an expected-vs-actual diff for a failing run without a
   * second fetch (gh #2813). Omitted when the snapshot carried no expected.
   */
  expected_response?: ExpectedResponseData;
  /**
   * The scenario's frozen multi-turn `message_history`, captured per-run in the
   * `eval_session_run.scenario_snapshot` at session-launch time and replayed to
   * the responder ahead of the trigger turn (gh #2625). Surfaced as the
   * eval-details "conversation so far" block so a reviewer judges the response
   * against the full thread that preceded the evaluated turn — the live `message`
   * table only holds the trigger + the agent's reply, never this history. Omitted
   * when the scenario had no prior turns.
   */
  scenario_history?: MessageHistoryItemResponse[];
  response_match: boolean | null;
  response_comment?: string;
  execution_time_ms?: number;
  run_status: RunStatus;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  scores?: Record<string, number>;
  metadata?: Record<string, unknown>;
  eval?: {
    eval_id: string;
    eval_name: string;
    responder_agent_id: string;
  } | null;
}

// -- Analytics response types --

export interface AgentPerformanceResponse {
  agent_id: string;
  agent_name: string;
  success_rate: number;
  total_runs: number;
  avg_execution_time: number;
}

export interface TrendDataResponse {
  date: string;
  success_rate: number;
  total_runs: number;
  avg_execution_time: number;
}

export interface ScoreDimensionData {
  dimension: string;
  avg_score: number;
  min_score: number;
  max_score: number;
  count: number;
}

export interface EvalAnalyticsData {
  totalEvals: number;
  enabledEvals: number;
  totalSessions: number;
  completedSessions: number;
  overallSuccessRate: number;
  avgExecutionTime: number;
  topPerformingAgents: AgentPerformanceResponse[];
  recentTrends: TrendDataResponse[];
  scoreDimensions: ScoreDimensionData[];
}

// -- Reference types (for dropdowns) --

export interface EvalAgentReference {
  agent_id: string;
  agent_name: string;
  agent_role: string;
}

export interface EvalHubReference {
  hub_id: string;
  hub_name: string;
  hub_description?: string;
}

// -- Pagination --

export interface EvalPaginationResponse {
  limit: number;
  offset: number;
  has_more: boolean;
}

// =============================================================================
// ENDPOINT RESPONSE WRAPPERS
// =============================================================================

export interface GetEvalsResponse {
  success: true;
  data: {
    evals: EvalWithAgentResponse[];
    total_count: number;
    pagination?: EvalPaginationResponse;
  };
}

export interface CreateEvalResponse {
  success: true;
  data: { eval: EvalResponse };
  message: string;
}

export interface UpdateEvalResponse {
  success: true;
  data: { eval: EvalResponse };
  message: string;
}

export interface DeleteEvalResponse {
  success: true;
  message: string;
}

export interface DeleteEvalErrorResponse {
  success: false;
  error: 'not_found';
  message: string;
}

export interface ToggleEvalResponse {
  success: true;
  data: { eval: EvalResponse };
  message: string;
}

export interface BulkToggleEvalsResponse {
  success: true;
  data: {
    updated_count: number;
    updated_eval_ids: string[];
  };
  message: string;
}

export interface GetSessionsResponse {
  success: true;
  data: {
    sessions: EvalSessionResponse[];
    total_count: number;
    pagination?: EvalPaginationResponse;
  };
}

export interface CreateSessionResponse {
  success: true;
  data: { session: EvalSessionResponse };
  message: string;
}

export interface RunSessionResponse {
  success: true;
  data: { session: EvalSessionResponse };
  message: string;
}

export interface StopSessionResponse {
  success: true;
  data: { session: EvalSessionResponse };
  message: string;
}

export interface DeleteSessionResponse {
  success: true;
  message: string;
  /** Number of eval conversations torn down alongside the session. */
  data?: { deleted_conversations: number };
}

export interface DeleteAllSessionsResponse {
  success: true;
  message: string;
  data: {
    /** Number of eval sessions deleted. */
    deleted_sessions: number;
    /** Number of eval conversations torn down across all deleted sessions. */
    deleted_conversations: number;
  };
}

export interface GetSessionDetailsResponse {
  success: true;
  data: {
    session: EvalSessionResponse;
    results: EvalSessionResultResponse[];
  };
}

export interface GetSessionResultsResponse {
  success: true;
  data: {
    results: EvalSessionResultResponse[];
    total_count: number;
    pagination?: EvalPaginationResponse;
  };
}

export interface GetSessionRunsResponse {
  success: true;
  data: {
    runs: EvalSessionRunResponse[];
    total_count: number;
    session_id: string;
    pagination?: EvalPaginationResponse;
  };
}

/** Per-session resource cost — sums tokens and billable operations over the
 *  unified `conversation` table where `is_eval = true AND eval_session_id = ?`.
 *  `billable_operations_total` matches the OrgDO billing increment.
 *  `cold` indicates ClickHouse cold-start; on cold, all numeric fields fall back to 0
 *  and the client may retry. */
export interface EvalSessionCost {
  conversations_count: number;
  tokens_total: number;
  tokens_input: number;
  tokens_output: number;
  billable_operations_total: number;
}

export interface GetEvalSessionCostResponse {
  success: true;
  data: {
    cost: EvalSessionCost;
    cold?: true;
  };
}

export interface GetEvalAnalyticsResponse {
  success: true;
  data: {
    analytics: EvalAnalyticsData;
  };
}

export interface ExportSessionResultsResponse {
  success: true;
  data: {
    download_url: string;
    filename: string;
    format: string;
  };
  message: string;
}

export interface ValidateEvalResponse {
  success: true;
  data: {
    is_valid: boolean;
    validation_errors: string[];
    suggestions?: string[];
  };
}

export interface GetHubAgentsResponse {
  success: true;
  data: {
    agents: EvalAgentReference[];
  };
}

export interface GetHubsListResponse {
  success: true;
  data: {
    hubs: EvalHubReference[];
  };
}

// -- Scenario set wrappers --

export interface GetScenarioSetsResponse {
  success: true;
  data: {
    scenario_sets: ScenarioSetResponse[];
    total_count: number;
  };
}

export interface GetScenarioSetResponse {
  success: true;
  data: { scenario_set: ScenarioSetResponse };
}

export interface CreateScenarioSetResponse {
  success: true;
  data: { scenario_set: ScenarioSetResponse };
  message: string;
}

export interface UpdateScenarioSetResponse {
  success: true;
  data: { scenario_set: ScenarioSetResponse };
  message: string;
}

export interface DeleteScenarioSetResponse {
  success: true;
  message: string;
}

export interface DeleteScenarioSetConflictResponse {
  success: false;
  error: 'scenario_set_not_empty';
  scenario_count: number;
  message: string;
}

export interface ListScenariosBySetResponse {
  success: true;
  data: {
    scenarios: EvalResponse[];
    total_count: number;
  };
}

// -- Journey wrappers --

export interface GetJourneysResponse {
  success: true;
  data: { journeys: JourneyResponse[]; total_count: number };
}

export interface GetJourneyResponse {
  success: true;
  data: { journey: JourneyResponse };
}

export interface CreateJourneyResponse {
  success: true;
  data: { journey: JourneyResponse };
  message: string;
}

export interface UpdateJourneyResponse {
  success: true;
  data: { journey: JourneyResponse };
  message: string;
}

export interface DeleteJourneyResponse {
  success: true;
  message: string;
}

export interface DeleteJourneyErrorResponse {
  success: false;
  error: 'not_found';
  message: string;
}

/** A journey run creates + runs a session targeting the journey's managed set. */
export interface RunJourneyResponse {
  success: true;
  data: { session: EvalSessionResponse };
  message: string;
}

// -- Compare sessions --

/** Per-scenario stats for a single session in a comparison. */
export interface CompareScenarioStats {
  total_runs: number;
  success_rate: number;
  avg_execution_time_ms: number;
  /** Per-dimension averages from `data.eval_scores.*` (excludes meta dimensions). */
  score_dimensions: Record<string, number>;
}

/** Aggregate session-level summary for the compare summary bar. */
export interface CompareSessionSummary {
  eval_session_id: string;
  session_name: string;
  scenario_set_id: string | null;
  scenario_set_name: string | null;
  agent: AgentConfigSnapshot | null;
  total_runs: number;
  success_rate: number;
  avg_execution_time_ms: number;
  tokens_total: number;
  billable_operations_total: number;
}

/** Per-scenario row in the diff table — only scenarios present in BOTH sessions. */
export interface CompareScenarioRow {
  eval_id: string;
  scenario_name: string;
  a: CompareScenarioStats;
  b: CompareScenarioStats;
  /** `b.success_rate - a.success_rate` (signed delta). */
  delta_success_rate: number;
}

export interface CompareSessionsResponse {
  success: true;
  data: {
    session_a: CompareSessionSummary;
    session_b: CompareSessionSummary;
    per_scenario: CompareScenarioRow[];
    /** True when session A and B targeted different scenario sets — UI shows a warning. */
    scenario_sets_match: boolean;
    /** `cold` flags ClickHouse cold-start; numeric fields fall back to zeros and the client may retry. */
    cold?: true;
  };
}

// -- Production replay (create scenario from conversation) --

export interface CreateScenarioFromConversationResponse {
  success: true;
  data: { eval: EvalResponse };
  message: string;
}

// -- Production replay (capture conversation as journey) --

export interface CreateJourneyFromConversationResponse {
  success: true;
  data: { journey: JourneyResponse };
  message: string;
}

// -- Read-only SQL (CLI agent surface) --

export interface EvalsSqlResponse {
  success: true;
  data: Record<string, unknown>[];
  statistics?: {
    elapsed: number;
    rows_read: number;
    bytes_read: number;
  };
  cold?: boolean;
}

export interface EvalsSqlSchemaPath {
  path: string;
  type: 'numeric' | 'categorical' | 'text' | 'unknown';
  origin: 'system' | 'eval_scores';
  category?: string;
  description?: string;
}

export interface EvalsSqlSchemaResponse {
  success: true;
  data: {
    table: 'conversation';
    top_level: Array<{ column: string; type: string }>;
    paths: EvalsSqlSchemaPath[];
  };
}
